import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {
  ComposerAiFeature,
  ComposerAiFeatureLanguagesEnum,
  ComposerAiFeatureTonesEnum,
  ComposerAiFeatureTypesEnum,
  renderedComposerAiLanguages,
  renderedComposerMenuOptionsAiTones,
} from '../../../../../../libs/shared/core/src/helpers/ai/composeV2Helpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {UnreachableError} from '../../../../../../libs/shared/core/src/helpers/types/customErrors';
import {collectEnumValues} from '../../../../../../libs/shared/core/src/models/api/types/enumTypes';
import {Dropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownList} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {SubmenuDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/submenuDropdownItem';
import {SimpleDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {AiComposeRefineDropdownTooltip} from './aiComposeRefineDropdownTooltip';

/*
 * Constants.
 */

const refinements = [
  {
    type: ComposerAiFeatureTypesEnum.FIX_SPELLING_AND_GRAMMAR,
    label: (
      <Trans id="ai-compose-refine-dropdown-item-fix-spelling-and-grammar">Fix spelling and grammar</Trans>
    ),
  },
  {
    type: ComposerAiFeatureTypesEnum.EXPAND,
    label: <Trans id="ai-compose-refine-dropdown-item-expand">Expand</Trans>,
  },
  {
    type: ComposerAiFeatureTypesEnum.MAKE_CONCISE,
    label: <Trans id="ai-compose-refine-dropdown-item-make-concise">Make more concise</Trans>,
  },
  {
    type: ComposerAiFeatureTypesEnum.CHANGE_TONE,
    label: <Trans id="ai-compose-refine-dropdown-item-change-tone">Change tone</Trans>,
  },
  {
    type: ComposerAiFeatureTypesEnum.TRANSLATE,
    label: <Trans id="ai-compose-refine-dropdown-item-translate">Translate</Trans>,
  },
] as const;

const tones = collectEnumValues(ComposerAiFeatureTonesEnum);
const languages = collectEnumValues(ComposerAiFeatureLanguagesEnum);

const tooltips: {[key: string]: ReactNode} = {
  [ComposerAiFeatureTypesEnum.EXPAND]: (
    <AiComposeRefineDropdownTooltip
      title={
        <Trans id="ai-compose-refine-dropdown-item-expand-tooltip-title">
          How to expand with AI effectively?
        </Trans>
      }
      tips={[
        {
          key: 'be-specific',
          tip: (
            <Trans id="ai-compose-refine-dropdown-item-expand-tooltip-be-specific">
              When outlining your draft, be as specific as possible to give AI essential details.
            </Trans>
          ),
        },
        {
          key: 'context-used',
          tip: (
            <Trans id="ai-compose-refine-dropdown-item-expand-tooltip-context-used">
              AI composes based on the both your existing draft and the messages in the conversation.
            </Trans>
          ),
        },
      ]}
    />
  ),
};

/*
 * Props.
 */

interface AiComposeRefineDropdownProps {
  popoverTransferProps: PopoverTransferProps;
  shouldHideHeader?: boolean;
  excludeFeatures: ReadonlyArray<ComposerAiFeatureTypesEnum>;
  isExclusive?: boolean;
  onAiFeature: (feature: ComposerAiFeature) => void;
}

/*
 * Style.
 */

const StyledHeaderDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 41px;
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  color: ${(p) => p.theme.greys.shade80};
  text-align: center;
  font-weight: ${fontWeights.semibold};
  ${ellipsis()};
`;

const StyledDropdownItemContainerDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

/*
 * Component.
 */

export const AiComposeRefineDropdown: FC<AiComposeRefineDropdownProps> = ({
  popoverTransferProps,
  excludeFeatures = [],
  ...props
}) => {
  const renderSubmenuItem = <T extends string>(
    parentType: string,
    parentLabel: ReactNode,
    items: ReadonlyArray<T>,
    renderedItems: Record<T, ReactNode>,
    onSelect: (item: T) => void,
  ) => (
    <SubmenuDropdownItem
      key={parentType}
      renderSubmenu={(dropdownProps) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <Dropdown {...dropdownProps}>
          <DropdownList>
            {items.map((item) => (
              <SimpleDropdownItem key={item} onSelect={() => onSelect(item)}>
                <StyledDropdownItemContainerDiv>{renderedItems[item]}</StyledDropdownItemContainerDiv>
              </SimpleDropdownItem>
            ))}
          </DropdownList>
        </Dropdown>
      )}
    >
      {parentLabel}
    </SubmenuDropdownItem>
  );

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Dropdown {...popoverTransferProps} isExclusive={props.isExclusive}>
      {!props.shouldHideHeader && (
        <StyledHeaderDiv>
          <Trans id="ai-compose-refine-dropdown-title">Compose with AI</Trans>
        </StyledHeaderDiv>
      )}
      <DropdownList>
        {refinements
          .filter(({type}) => !excludeFeatures.includes(type))
          .map(({type, label}) => {
            switch (type) {
              case ComposerAiFeatureTypesEnum.FIX_SPELLING_AND_GRAMMAR:
              case ComposerAiFeatureTypesEnum.EXPAND:
              case ComposerAiFeatureTypesEnum.MAKE_CONCISE:
                return (
                  <SimpleDropdownItem key={type} onSelect={() => props.onAiFeature({type})}>
                    <StyledDropdownItemContainerDiv>
                      {label}
                      {tooltips[type]}
                    </StyledDropdownItemContainerDiv>
                  </SimpleDropdownItem>
                );

              case ComposerAiFeatureTypesEnum.CHANGE_TONE:
                return renderSubmenuItem(type, label, tones, renderedComposerMenuOptionsAiTones, (tone) =>
                  props.onAiFeature({type, tone}),
                );

              case ComposerAiFeatureTypesEnum.TRANSLATE: {
                return renderSubmenuItem(type, label, languages, renderedComposerAiLanguages, (language) =>
                  props.onAiFeature({type, language}),
                );
              }

              default:
                throw new UnreachableError(type);
            }
          })}
      </DropdownList>
    </Dropdown>
  );
};
